import { channelInfo } from '@/pages/opinion/components/ChannelLabel';
import {
  arrayToObj,
  getPercentageInfo,
  getPercentageValue,
  getShowCount,
  transRegion,
} from '@/pages/opinion/utils';
import { mergeSameKeyword } from '@/pages/opinion/utils/keyword';

// 图表提示框
export const heroEchartsFormatterFunc = (
  param: any,
  timelineType: string,
  splitText = '#_to_#',
) => {
  let xAxisTitle = '';
  param?.forEach((item: any) => {
    let { data, axisValue } = item || {};
    if (!xAxisTitle) {
      xAxisTitle = axisValue;
    }
  });
  const isPercentage = timelineType === 'non-negative';

  const isSentiment = timelineType === 'sentiment';
  let returnStr = `<div class="common-bar-line-tooltip-box">
  <div class="item-title">
     <span class="left">${xAxisTitle}</span>
     <span class="center">${
       isPercentage ? 'Non Negative%' : isSentiment ? 'Sentiment' : 'Mentions'
     }</span>
  </div>
`;
  param?.sort((a: any, b: any) => b.data - a.data);
  param?.forEach((item: any) => {
    let { color, data, seriesName } = item || {};

    let imgIcon = null;
    if (seriesName?.includes(splitText)) {
      const [channel, name] = seriesName.split(splitText);
      const { img } = channelInfo(channel);
      imgIcon = img;
      seriesName = '#' + name;
    }
    let realData = getShowCount(data);
    if (isPercentage) {
      realData = (data * 100).toFixed(1) + '%';
    } else if (isSentiment) {
      realData = realData.toFixed(2);
    }
    if (data === 0) {
      realData = '-';
    }
    returnStr += `<div class="item-body"  style="height:22px;">
        <span class="left-data">
            <i style="background-color: ${color}"></i>
            ${
              imgIcon === null
                ? ''
                : `<img  style="width:16px;height:16px;margin: 0px 5px;vertical-align: sub"src="${imgIcon}"/>`
            }
            <span class="text" style="color: #888">${seriesName}</span>
        </span>
        <span class="center-data">${realData}</span>
    </div>`;
  });
  return returnStr + '</div>';
};

/**
 * 创建计算同比变化的函数
 * @param item    新的对象
 * @param oldItem 老的对象
 * @returns (key: string) => ({ [key: string]: number }) 返回一个传key就能计算同比对象的函数
 */
function calculateRatioFn(
  item: { [key: string]: number },
  oldItem: { [key: string]: number },
) {
  return (key: string) => {
    const newValue = item?.[key];
    const oldValue = oldItem?.[key];
    let value = !newValue ? 0 : 100;
    if (oldValue) {
      // 同比变化的计算公式： （新 - 旧）/ 旧,最终值为百分比，所以×100，然后4舍5入保留两位小数
      value = Number((((newValue - oldValue) / oldValue) * 100).toFixed(2));
    }
    return { [key + '_than']: value };
  };
}

/**
 * 创建计算变化的函数
 * @param item    原始对象
 * @param oldItem 总数的对象
 * @returns (key: string) => ({ [key: string]: number }) 返回一个传key就能计算同比对象的函数
 */
function calculatePercentageFn(
  item: { [key: string]: number },
  oldItem: { [key: string]: number },
) {
  return (key: string) => {
    const newValue = item?.[key];
    const oldValue = oldItem?.[key];
    return {
      [key + '_percentage']: getPercentageValue(newValue, oldValue) * 100,
    };
  };
}

/**
 * 批量计算同比变化的函数
 * @param list            需要计算同比变化的属性key
 * @param calculateRatio  计算同比函数的方法
 */
function batchCalculateRatio(
  list: Array<string>,
  calculateRatio: (key: string) => { [key: string]: number | string },
) {
  return list.reduce((prev, curr) => {
    return { ...prev, ...calculateRatio(curr) };
  }, {});
}

/**
 * 处理表格数据，并添加同比变化的内容
 * @param newList
 * @param oldList
 * @returns
 */
export function dealwithTableData(newList: any[], oldList: any[], max?: any) {
  if (!newList || newList.length === 0) return [];
  const keys = [
    'mentions',
    'negative_mentions',
    'non_negative_ratio',
    'sentiment',
    'views',
    'followers',
  ];
  const oldMap = arrayToObj(oldList, 'name');
  return newList.map((item) => {
    const key = item.name;
    const thanMap = batchCalculateRatio(
      keys,
      calculateRatioFn(item, oldMap?.[key]),
    );
    let ratioMap = {};
    if (max) {
      ratioMap = batchCalculateRatio(keys, calculatePercentageFn(item, max));
    }
    return {
      ...item,
      ...thanMap,
      ...ratioMap,
    };
  });
}

export function getSentimentValue(value: number) {
  // 向下取整，但是最后保留一位小数是为了添加.0  【3 -> 3.0】
  return (Math.floor(value * 10) / 10).toFixed(1);
}

export function dealwithKeyword(keywordList: Array<any>, language: string) {
  return mergeSameKeyword(keywordList, language);
}

export function dealwithCondHero({ hero }: any) {
  const map: any = {};
  hero?.forEach((item: any) => {
    map[item.name_en] = {
      cn: item.name_zh,
      en: item.name_en,
    };
  });
  return map;
}

// 转换名称
function translateName(item: any, type: string) {
  switch (type) {
    case 'country':
      return transRegion(item, 1)?.toLowerCase() === 'global'
        ? 'Unidentified'
        : transRegion(item, 1);
    case 'market':
      return transRegion(item, 1);
    case 'language':
      return transRegion(item, 3);
    default:
      return item;
  }
}

export function dealwithLeftInfoData(data: {
  language: Array<any>;
  country: Array<any>;
  channel: Array<any>;
}) {
  const result: any = { language: [], country: [], channel: [] };
  for (const key in result) {
    const dataElement = data[key];
    const total = dataElement?.reduce((result: any, pre: any) => {
      return result + pre.mentions;
    }, 0);

    result[key] = dataElement?.map((item: any) => {
      const name = item[key];
      const trans = translateName(name, key);
      return {
        name: name,
        text: trans,
        progress: getPercentageInfo(item.mentions, total),
        value: item.mentions,
      };
    });

    result[key]?.sort((a: any, b: any) => b.value - a.value);
  }

  return result;
}
